// F-V operating-point panel for the Results pane.
//
// For each corner the displayed run explicitly edits (damper_code + all four
// clicks), this draws the bench F-V curve that choice resolves to and rides a
// dot along it at the instantaneous damper velocity under the shared cursor.
// Velocity is differentiated client-side from the predicted damper-position
// trace (metres) against the waveform time axis and converted to in/s; the
// curve force axis is lbf, matching the edit-sheet preview convention.
//
// The dot is hooked to the "damper-overlay" cursor sync indirectly: the Results
// pane lifts the cursor index out of the overlay chart (which is a member of
// that sync group), so hovering ANY synced chart moves every dot here. The DOM
// write that repositions the dot is throttled with requestAnimationFrame.
//
// Availability: a corner appears only when its curve resolves; when the run
// edits no resolvable corner (the R0 baseline case) the whole panel hides.
import { useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";

import type { Run, DamperSettingPayload } from "../api/tests";
import type { WaveformJson } from "../api/runs";
import {
  fetchDamperCurve,
  type DamperCurveResponse,
} from "../api/damperCurve";
import {
  centralDifference,
  interpLinear,
  operatingPoint,
  toDisplayCurve,
  type DisplayCurve,
  type OperatingPoint,
} from "./fvOperatingPoint";

const CORNERS = ["lf", "rf", "lr", "rr"] as const;
type Corner = (typeof CORNERS)[number];
const CORNER_LABEL: Record<Corner, string> = {
  lf: "LF", rf: "RF", lr: "LR", rr: "RR",
};

const CANDIDATE_COLOUR = "#5aa9e6";
const BASELINE_COLOUR = "#9a9a9a";

type ResolvedCorner = {
  corner: Corner;
  damperCode: string;
  clicks: [number, number, number, number];
};

// A corner is resolvable when the run explicitly carries a damper_code plus all
// four finite clicks for it. Baseline-inherited corners carry neither and are
// skipped (there is no single code+clicks to draw a curve for).
export function resolveCorners(run: Run): ResolvedCorner[] {
  const setting = run.damper_setting as DamperSettingPayload | null | undefined;
  const corners = setting?.corners ?? {};
  const out: ResolvedCorner[] = [];
  for (const c of CORNERS) {
    const edit = corners[c];
    if (!edit) continue;
    const code =
      typeof edit.damper_code === "string" ? edit.damper_code.trim() : "";
    const clicks = edit.clicks;
    if (
      code.length > 0 &&
      Array.isArray(clicks) &&
      clicks.length === 4 &&
      clicks.every((n) => Number.isFinite(n))
    ) {
      out.push({
        corner: c,
        damperCode: code,
        clicks: [clicks[0], clicks[1], clicks[2], clicks[3]],
      });
    }
  }
  return out;
}

// Candidate (+ optional baseline) on a shared union velocity grid, display
// units. Baseline stays a fixed all-null series when absent so the series count
// is stable.
function buildFvData(
  cand: DisplayCurve,
  base: DisplayCurve | null,
): uPlot.AlignedData {
  const xs = Array.from(
    new Set([...cand.velInPs, ...(base ? base.velInPs : [])]),
  ).sort((a, b) => a - b);
  const candY = xs.map((x) => interpLinear(cand.velInPs, cand.forceLbf, x));
  const baseY = base
    ? xs.map((x) => interpLinear(base.velInPs, base.forceLbf, x))
    : xs.map(() => null);
  return [xs, candY, baseY] as uPlot.AlignedData;
}

function fvOptions(): uPlot.Options {
  const axis = { stroke: "#9a9a9a", grid: { stroke: "#2a2a2a", dash: [2, 2] } };
  return {
    width: 230,
    height: 140,
    scales: { x: { time: false } },
    legend: { show: false },
    cursor: { show: false },
    series: [
      {},
      { label: "candidate", stroke: CANDIDATE_COLOUR, width: 1.5 },
      { label: "baseline", stroke: BASELINE_COLOUR, width: 1, dash: [4, 4] },
    ],
    axes: [
      { ...axis, label: "velocity (in/s)" },
      { ...axis, label: "force (lbf)" },
    ],
  };
}

function captionText(resp: DamperCurveResponse | null): string {
  if (!resp) return "resolving bench curve…";
  if (!resp.available) return resp.reason ?? "no matching bench curve";
  const m = resp.match;
  if (!m) return "bench curve";
  if (m.confidence === "exact") return "exact curve";
  if (m.confidence === "nearest") {
    const cl = m.matched_clicks ? ` [${m.matched_clicks.join(", ")}]` : "";
    return `nearest curve${cl}`;
  }
  if (m.confidence === "code_only") return "nearest curve (code only)";
  return `bench curve (${m.confidence})`;
}

// One corner's mount-once uPlot with a DOM dot at the operating point. uPlot
// needs a real 2D context (absent under JSDOM), so construction is guarded: a
// chart that cannot mount leaves the caption intact.
function FvCurveChart({
  corner,
  display,
  baseline,
  op,
}: {
  corner: Corner;
  display: DisplayCurve;
  baseline: DisplayCurve | null;
  op: OperatingPoint | null;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const dotRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const dataRef = useRef<uPlot.AlignedData>(buildFvData(display, baseline));
  dataRef.current = buildFvData(display, baseline);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    try {
      const u = new uPlot(fvOptions(), dataRef.current, host);
      chartRef.current = u;
      const dot = document.createElement("div");
      dot.className = "fv-op-dot";
      dot.setAttribute("data-testid", `fv-op-dot-${corner}`);
      dot.style.display = "none";
      u.over.appendChild(dot);
      dotRef.current = dot;
    } catch {
      chartRef.current = null;
    }
    return () => {
      if (rafRef.current !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
      dotRef.current = null;
      chartRef.current?.destroy();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corner]);

  // Keep the curve data current if the resolved curve changes under us.
  useEffect(() => {
    const u = chartRef.current;
    if (!u) return;
    try {
      u.setData(dataRef.current);
    } catch {
      /* inert without a live chart */
    }
  }, [display, baseline]);

  // Ride the dot to the operating point. Throttled through rAF so a fast scrub
  // coalesces to one DOM write per frame.
  useEffect(() => {
    const apply = () => {
      rafRef.current = null;
      const u = chartRef.current;
      const dot = dotRef.current;
      if (!u || !dot) return;
      if (!op) {
        dot.style.display = "none";
        return;
      }
      let left: number;
      let top: number;
      try {
        left = u.valToPos(op.velInPs, "x");
        top = u.valToPos(op.forceLbf, "y");
      } catch {
        dot.style.display = "none";
        return;
      }
      if (!Number.isFinite(left) || !Number.isFinite(top)) {
        dot.style.display = "none";
        return;
      }
      dot.style.display = "block";
      dot.style.left = `${left}px`;
      dot.style.top = `${top}px`;
    };
    if (typeof requestAnimationFrame === "function") {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(apply);
    } else {
      apply();
    }
    return () => {
      if (rafRef.current !== null && typeof cancelAnimationFrame === "function") {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [op]);

  return <div ref={hostRef} data-testid={`fv-op-canvas-${corner}`} />;
}

function FvCornerCell({
  testId,
  corner,
  damperCode,
  clicks,
  predicted,
  timeS,
  cursorIdx,
}: {
  testId: number;
  corner: Corner;
  damperCode: string;
  clicks: [number, number, number, number];
  predicted: number[];
  timeS: number[];
  cursorIdx: number | null;
}) {
  const [resp, setResp] = useState<DamperCurveResponse | null>(null);
  const clicksKey = clicks.join(",");

  useEffect(() => {
    const controller = new AbortController();
    setResp(null);
    fetchDamperCurve(
      { testId, corner, damperCode, clicks },
      { signal: controller.signal },
    )
      .then((r) => setResp(r))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        // Surface as "unavailable" so the cell shows a caption, not a crash.
        setResp({ available: false, reason: "preview unavailable" });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId, corner, damperCode, clicksKey]);

  const velocityMps = useMemo(
    () => centralDifference(predicted, timeS),
    [predicted, timeS],
  );
  const display = useMemo(
    () => (resp?.available && resp.curve ? toDisplayCurve(resp.curve) : null),
    [resp],
  );
  const baseline = useMemo(
    () => (resp?.available && resp.baseline ? toDisplayCurve(resp.baseline) : null),
    [resp],
  );
  const op = useMemo(
    () => (display ? operatingPoint(velocityMps, display, cursorIdx) : null),
    [display, velocityMps, cursorIdx],
  );

  return (
    <div className="fv-op-cell" data-testid={`fv-op-cell-${corner}`}>
      <div className="fv-op-cell-head">
        <span className="fv-op-corner">{CORNER_LABEL[corner]}</span>
        <span className="fv-op-code">{damperCode}</span>
      </div>
      {display ? (
        <FvCurveChart
          corner={corner}
          display={display}
          baseline={baseline}
          op={op}
        />
      ) : null}
      <div className="fv-op-caption muted" data-testid={`fv-op-caption-${corner}`}>
        {op
          ? `${op.velInPs.toFixed(1)} in/s · ${op.forceLbf.toFixed(0)} lbf`
          : captionText(resp)}
      </div>
    </div>
  );
}

export type FvOperatingPointPanelProps = {
  testId: number;
  run: Run;
  waveform: WaveformJson;
  cursorIdx: number | null;
};

export function FvOperatingPointPanel({
  testId,
  run,
  waveform,
  cursorIdx,
}: FvOperatingPointPanelProps) {
  const resolved = useMemo(() => resolveCorners(run), [run]);
  if (resolved.length === 0) return null;
  return (
    <div className="fv-op-panel" data-testid="fv-op-panel">
      <div className="fv-op-panel-head">
        F-V operating point
        <span className="muted fv-op-hint">dot rides each curve at the cursor</span>
      </div>
      <div className="fv-op-grid">
        {resolved.map((r) => (
          <FvCornerCell
            key={r.corner}
            testId={testId}
            corner={r.corner}
            damperCode={r.damperCode}
            clicks={r.clicks}
            predicted={waveform.predicted[r.corner]}
            timeS={waveform.time_s}
            cursorIdx={cursorIdx}
          />
        ))}
      </div>
    </div>
  );
}
